import QrCode from "@/modules/common/icons/qr-code"
import VisaMastercard from "@/modules/common/icons/visa-mastercard"
import React from "react"

export type PaymentMethodInfo = {
  title: string
  icon: React.JSX.Element
  description?: string
}

/* Map of payment provider_id to their title and icon. Only showing payment methods actually linked to the region. */
export const paymentInfoMap: Record<string, PaymentMethodInfo> = {
  pp_bankart_bankart: {
    title: "Kreditna kartica",
    icon: <VisaMastercard size="20" />,
  },
  "pp_bank-transfer_bank-transfer": {
    title: "Bančno nakazilo",
    icon: <QrCode size="22" />,
    description: "Plačilo z UPN QR kodo",
  },
}

// Bankart gateway — embedded card fields with optional 3DS redirect
export const isBankart = (providerId?: string): boolean => {
  return providerId === "pp_bankart_bankart"
}

/**
 * Hosted-page session from express checkout — not valid for the embedded widget
 * flow. Must match the `returnType === "REDIRECT"` check used in the payment
 * button, otherwise a valid embedded 3DS session (which also carries a
 * `redirectUrl`) could be mistaken for a legacy one and cleared.
 */
export const isLegacyHostedBankartSession = (
  session?: {
    provider_id?: string
    data?: { redirectUrl?: string; embedded_flow?: boolean; returnType?: string }
  } | null
): boolean =>
  isBankart(session?.provider_id) &&
  !!session?.data?.redirectUrl &&
  session?.data?.returnType === "REDIRECT" &&
  !session?.data?.embedded_flow

// Check if payment method is bank transfer
export const isBankTransfer = (providerId: string): boolean => {
  return providerId === "pp_bank-transfer_bank-transfer"
}

// Check if payment method is PayPal
export const isPaypal = (providerId: string): boolean => {
  return providerId?.includes("paypal") || providerId === "pp_paypal_paypal"
}

// Check if payment method is manual/test payment
export const isManual = (providerId: string): boolean => {
  return providerId?.includes("manual") || providerId === "pp_system_default"
}

// Get available payment methods for the region
export const getAvailablePaymentMethods = () => {
  return Object.keys(paymentInfoMap)
}

// Helper function to get payment method title with fallback
export const getPaymentMethodTitle = (providerId?: string) => {
  if (!providerId) return "Neznana plačilna metoda"
  
  // Check if we have a direct mapping
  if (paymentInfoMap[providerId]) {
    return paymentInfoMap[providerId].title
  }
  
  // Fallback logic for known provider types
  if (providerId.includes("pp_system")) {
    return "Plačilo po računu"
  }
  if (providerId.includes("bank-transfer")) {
    return "Bančno nakazilo"
  }
  if (providerId.includes("stripe") || providerId.includes("bankart")) {
    return "Kreditna kartica"
  }
  if (providerId.includes("paypal")) {
    return "PayPal"
  }
  
  // Last resort: return the provider ID
  return providerId
}

export const currencySymbolMap: Record<string, string> = {
  usd: "$",
  eur: "€",
  gbp: "£",
  cad: "C$",
  aud: "A$",
  jpy: "¥",
  cny: "¥",
  chf: "CHF",
  hkd: "HK$",
  nzd: "NZ$",
  sek: "kr",
  krw: "₩",
  sgd: "S$",
  nok: "kr",
  mxn: "$",
  inr: "₹",
  rub: "₽",
  zar: "R",
  try: "₺",
  brl: "R$",
  twd: "NT$",
  dkk: "kr",
  pln: "zł",
  thb: "฿",
  idr: "Rp",
  huf: "Ft",
  czk: "Kč",
  ils: "₪",
  clp: "$",
  php: "₱",
  aed: "د.إ",
  cop: "$",
  sar: "﷼",
  myr: "RM",
  ron: "lei",
  vnd: "₫",
  egp: "£",
  pkr: "₨",
  ngn: "₦",
  bdt: "৳",
  uah: "₴",
  kes: "KSh",
  ars: "$",
  qar: "﷼",
  kwd: "د.ك",
  omr: "﷼",
  bhd: "ب.د",
  lkr: "₨",
  mmk: "K",
  uzs: "лв",
}
